import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { WA_NUMBER, waLink } from "@/lib/contacts";
import { PageHeader, Toolbar, ToolbarInput, ToolbarSelect, Drawer, fmtINR, timeAgo } from "./_shared";
import AdminDeleteControls from "@/components/admin/AdminDeleteControls";

// 8-stage CRM lifecycle. Keep order — used for the status filter and the pipeline picker.
const STAGES = [
  { v: "new_requirement",   l: "New Requirement",   color: "#22C55E" },
  { v: "reviewing",         l: "Reviewing",         color: "#6FA8DC" },
  { v: "matching_vehicles", l: "Matching Vehicles", color: "#A78BFA" },
  { v: "contacted",         l: "Contacted",         color: "#F5A623" },
  { v: "vehicles_shared",   l: "Vehicles Shared",   color: "#E0A86A" },
  { v: "negotiating",       l: "Negotiating",       color: "#FF8C42" },
  { v: "closed_won",        l: "Closed Won",        color: "#FF1E1E" },
  { v: "closed_lost",       l: "Closed Lost",       color: "#808080" },
];
const STAGE_LABEL = STAGES.reduce((a, s) => ({ ...a, [s.v]: s.l }), {});
const STAGE_COLOR = STAGES.reduce((a, s) => ({ ...a, [s.v]: s.color }), {});

const INTENT_FILTER_OPTIONS = [
  { v: "all", l: "All Timelines" },
  { v: "immediate", l: "Immediate" },
  { v: "this_week", l: "This Week" },
  { v: "this_month", l: "This Month" },
  { v: "exploring", l: "Exploring" },
];
const STATE_FILTER_OPTIONS = [
  { v: "all", l: "All Statuses" },
  ...STAGES.map(s => ({ v: s.v, l: s.l })),
];

export default function BuyerReqs() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [intent, setIntent] = useState("all");
  const [stat, setStat] = useState("all");
  const [openId, setOpenId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [exporting, setExporting] = useState(false);

  const load = () => api.get("/admin/buyer-requirements").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = [...items];
    if (q) list = list.filter(r => `${r.full_name} ${r.contact_phone} ${(r.brands || []).join(" ")}`.toLowerCase().includes(q.toLowerCase()));
    if (intent !== "all") list = list.filter(r => r.intent === intent);
    if (stat !== "all") list = list.filter(r => (r.status || "new_requirement") === stat);
    list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return list;
  }, [items, q, intent, stat]);

  const opened = openId ? items.find(r => r.id === openId) : null;

  // ----- Excel export (auth via Bearer; download via blob to keep token out of URL) -----
  const downloadXlsx = async (params, label) => {
    setExporting(true);
    const t = toast.loading(`Preparing ${label} export…`);
    try {
      const res = await api.get("/admin/buyer-requirements/export.xlsx", {
        params,
        responseType: "blob",
        timeout: 60000,
      });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const stamp = new Date().toISOString().slice(0, 16).replace(/[:T-]/g, "");
      const a = document.createElement("a");
      a.href = url;
      a.download = `qdrives-buyer-requirements-${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${label}`, { id: t });
    } catch (e) {
      toast.error(`Export failed: ${e?.message || "unknown error"}`, { id: t });
    } finally {
      setExporting(false);
    }
  };

  const exportAll = () => downloadXlsx({}, "all requirements");
  const exportFiltered = () => {
    const p = {};
    if (stat !== "all") p.status = stat;
    if (intent !== "all") p.timeline = intent;
    downloadXlsx(p, "filtered");
  };
  const exportSelected = () => {
    if (selected.size === 0) return toast.error("Select at least one requirement");
    downloadXlsx({ ids: Array.from(selected).join(",") }, `${selected.size} selected`);
  };

  const toggleRow = (id, e) => {
    e.stopPropagation();
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected(prev => prev.size === filtered.length ? new Set() : new Set(filtered.map(r => r.id)));
  };

  return (
    <div data-testid="admin-buyer-reqs">
      <PageHeader title="Buyer Requirements" subtitle="Sourcing CRM" right={
        <ExportToolbar
          onAll={exportAll}
          onFiltered={exportFiltered}
          onSelected={exportSelected}
          selectedCount={selected.size}
          disabled={exporting}
        />
      }/>

      <div className="bg-[#0E0E0E] p-4 sm:p-5 mb-6">
        <Toolbar>
          <ToolbarInput value={q} onChange={setQ} placeholder="Search name / phone / brand…" testid="reqs-search" />
          <ToolbarSelect value={intent} onChange={setIntent} testid="reqs-intent" options={INTENT_FILTER_OPTIONS} />
          <ToolbarSelect value={stat} onChange={setStat} testid="reqs-status" options={STATE_FILTER_OPTIONS} />
          <div className="font-mono-data text-[10px] tracking-widest text-white/40 ml-auto">
            {filtered.length} buyers{selected.size > 0 && ` · ${selected.size} selected`}
          </div>
        </Toolbar>

        {/* Destructive actions row — Delete Selected always shown (disabled when none selected),
            Delete All only shown to Super Admin. */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <AdminDeleteControls
            resource="requirements"
            selectedIds={selected}
            totalCount={items.length}
            bulkEndpoint="/admin/requirements/bulk-delete"
            allEndpoint="/admin/requirements/all"
            testidPrefix="reqs"
            onDeleted={() => { setSelected(new Set()); load(); }}
          />
        </div>

        <div className="overflow-x-auto">
          <table className="term-table w-full">
            <thead>
              <tr>
                <th className="w-8"><input type="checkbox" checked={filtered.length > 0 && selected.size === filtered.length} onChange={toggleAll} data-testid="reqs-select-all" /></th>
                <th>Customer</th>
                <th>Phone</th>
                <th className="text-right">Budget</th>
                <th>Brands</th>
                <th>Body</th>
                <th>Timeline</th>
                <th>Finance</th>
                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const status = r.status || "new_requirement";
                return (
                  <tr key={r.id} onClick={() => setOpenId(r.id)} className="cursor-pointer" data-testid={`req-row-${r.id}`}>
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selected.has(r.id)}
                        onChange={(e) => toggleRow(r.id, e)}
                        data-testid={`reqs-select-${r.id}`}
                      />
                    </td>
                    <td className="text-white font-bold">{r.full_name || "—"}</td>
                    <td className="text-white/60 font-mono-data">{r.contact_phone || "—"}</td>
                    <td className="text-right">{fmtINR(r.budget_min)}–{fmtINR(r.budget_max)}</td>
                    <td className="text-white/60">{(r.brands || []).slice(0, 2).join(", ") || "—"}</td>
                    <td className="text-white/60">{(r.body_types || []).slice(0, 2).join(", ") || "—"}</td>
                    <td className="text-[#FF1E1E] text-[10px] tracking-widest uppercase">{r.intent || "—"}</td>
                    <td className="text-white/70 text-[11px]">{r.finance_required ? "Yes" : "No"}</td>
                    <td><StatusDot status={status} /></td>
                    <td className="text-white/50">{timeAgo(r.created_at)}</td>
                  </tr>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan={10} className="text-center text-white/40 py-8">No buyer requirements</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Drawer open={!!opened} onClose={() => setOpenId(null)} title={opened ? `${opened.full_name || "Unnamed"} · ${opened.contact_phone || "no phone"}` : ""}>
        {opened && <ReqDetail r={opened} onChange={load} />}
      </Drawer>
    </div>
  );
}

function ExportToolbar({ onAll, onFiltered, onSelected, selectedCount, disabled }) {
  // Desktop: 3 buttons. Mobile: collapse into a single "Export" dropdown.
  const [open, setOpen] = useState(false);
  return (
    <>
      {/* Desktop */}
      <div className="hidden sm:flex items-center gap-2">
        <button data-testid="export-all-btn" disabled={disabled} onClick={onAll} className="btn-secondary text-[10px] py-2 px-3">Export All</button>
        <button data-testid="export-filtered-btn" disabled={disabled} onClick={onFiltered} className="btn-secondary text-[10px] py-2 px-3">Export Filtered</button>
        <button
          data-testid="export-selected-btn"
          disabled={disabled || selectedCount === 0}
          onClick={onSelected}
          className="btn-primary text-[10px] py-2 px-3"
        >
          Export Selected{selectedCount > 0 ? ` (${selectedCount})` : ""}
        </button>
      </div>
      {/* Mobile dropdown */}
      <div className="sm:hidden relative">
        <button data-testid="export-menu-btn" onClick={() => setOpen(o => !o)} className="btn-secondary text-[10px] py-2 px-3">Export ▾</button>
        {open && (
          <div className="absolute right-0 top-full mt-1 z-30 bg-[#0F0F0F] border border-[#2A2A2A] min-w-[180px]">
            <button data-testid="export-all-mobile" onClick={() => { setOpen(false); onAll(); }} className="block w-full text-left text-[12px] px-3 py-2 text-white/85 hover:bg-[#1A1A1A]">Export All</button>
            <button data-testid="export-filtered-mobile" onClick={() => { setOpen(false); onFiltered(); }} className="block w-full text-left text-[12px] px-3 py-2 text-white/85 hover:bg-[#1A1A1A]">Export Filtered</button>
            <button
              data-testid="export-selected-mobile"
              disabled={selectedCount === 0}
              onClick={() => { setOpen(false); onSelected(); }}
              className="block w-full text-left text-[12px] px-3 py-2 text-white/85 hover:bg-[#1A1A1A] disabled:opacity-40"
            >
              Export Selected{selectedCount > 0 ? ` (${selectedCount})` : ""}
            </button>
          </div>
        )}
      </div>
    </>
  );
}

function StatusDot({ status }) {
  const color = STAGE_COLOR[status] || "#808080";
  const label = STAGE_LABEL[status] || status;
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] font-mono-data tracking-widest uppercase whitespace-nowrap" style={{ color }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />{label}
    </span>
  );
}

function ReqDetail({ r, onChange }) {
  const [suggestions, setSuggestions] = useState([]);
  const [note, setNote] = useState("");
  const [executiveInput, setExecutiveInput] = useState(r.assigned_executive || "");
  const [staffUsers, setStaffUsers] = useState([]);

  useEffect(() => {
    api.get(`/admin/matching/for-requirement/${r.id}`)
      .then(res => setSuggestions(res.data.suggestions || []))
      .catch(() => setSuggestions([]));
    api.get("/admin/users").then(res => setStaffUsers(res.data || [])).catch(() => setStaffUsers([]));
  }, [r.id]);

  const updateStatus = async (s) => {
    try {
      await api.patch(`/admin/requirements/${r.id}/status`, null, { params: { status: s } });
      toast.success(`Status → ${STAGE_LABEL[s] || s}`);
      onChange();
    } catch (e) { toast.error("Status update failed"); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/admin/requirements/${r.id}/note`, { text: note.trim() });
      setNote("");
      onChange();
      toast.success("Note added");
    } catch (e) { toast.error("Could not add note"); }
  };

  const saveExecutive = async () => {
    try {
      await api.patch(`/admin/requirements/${r.id}/assign`, { executive: executiveInput });
      onChange();
      toast.success(executiveInput.trim() ? `Assigned to ${executiveInput.trim()}` : "Unassigned");
    } catch (e) { toast.error("Assignment failed"); }
  };

  const saveExecutiveById = async (uid) => {
    try {
      await api.patch(`/admin/requirements/${r.id}/assign`, { assigned_to_user_id: uid || "" });
      onChange();
      toast.success(uid ? "Assigned" : "Unassigned");
    } catch (e) { toast.error("Assignment failed"); }
  };

  const markContacted = () => updateStatus("contacted");

  const copySummary = async () => {
    const lines = [
      `BUYER REQUIREMENT — ${r.full_name || "Unnamed"}`,
      `Phone: ${r.contact_phone || "—"}`,
      `Budget: ${fmtINR(r.budget_min)} – ${fmtINR(r.budget_max)}`,
      `Brands: ${(r.brands || []).join(", ") || "Any"}`,
      `Body: ${(r.body_types || []).join(", ") || "Any"}`,
      `Fuel: ${(r.fuel_types || []).join(", ") || "Any"}`,
      `Transmission: ${(r.transmissions || []).join(", ") || "Any"}`,
      `Year: ${r.year_min || "—"} – ${r.year_max || "—"}`,
      `Max KM: ${(r.max_km || 0).toLocaleString()}`,
      `Timeline: ${r.intent || "—"}`,
      `Finance: ${r.finance_required ? "Yes" : "No"}  |  Exchange: ${r.exchange ? "Yes" : "No"}`,
      `Status: ${STAGE_LABEL[r.status] || r.status}`,
    ];
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed — your browser blocked it");
    }
  };

  const phoneE164 = (r.contact_phone || "").replace(/[^\d+]/g, "");
  const phoneForWA = phoneE164.replace(/^\+/, "");
  const summaryForWA = `Hi ${r.full_name || "there"}, this is Q Drives following up on your ${r.intent || ""} requirement for ${(r.brands || []).join("/") || "a vehicle"}.`;

  return (
    <div className="space-y-6 text-sm">
      {/* Action bar */}
      <div className="flex flex-wrap gap-2" data-testid="req-action-bar">
        <a
          href={`tel:${phoneE164}`}
          data-testid="req-call-btn"
          className={`btn-secondary text-[11px] py-2 px-3 ${!phoneE164 ? "opacity-40 pointer-events-none" : ""}`}
        >Call</a>
        <a
          href={waLink(summaryForWA, phoneForWA || undefined)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="req-wa-btn"
          className={`btn-secondary text-[11px] py-2 px-3 ${!phoneForWA ? "opacity-40 pointer-events-none" : ""}`}
        >WhatsApp Customer</a>
        <button data-testid="req-copy-btn" onClick={copySummary} className="btn-secondary text-[11px] py-2 px-3">Copy Summary</button>
        <button
          data-testid="req-mark-contacted-btn"
          onClick={markContacted}
          className="btn-primary text-[11px] py-2 px-3"
          disabled={r.status === "contacted"}
        >Mark Contacted</button>
        {!phoneE164 && <div className="text-[10px] text-white/40 self-center">No phone on record — Call/WhatsApp disabled.</div>}
      </div>

      {/* SECTION 1 — Budget + Brands */}
      <Section title="Budget + Brands">
        <Row k="Budget" v={`${fmtINR(r.budget_min)} – ${fmtINR(r.budget_max)}`} />
        <Row k="Preferred Brands" v={(r.brands || []).join(", ") || "Any"} />
        <Row k="Body Types" v={(r.body_types || []).join(", ") || "Any"} />
        <Row k="Preferred Model" v={r.preferred_model || "—"} />
      </Section>

      {/* SECTION 2 — Vehicle Preferences */}
      <Section title="Vehicle Preferences">
        <Row k="Fuel Types" v={(r.fuel_types || []).join(", ") || "Any"} />
        <Row k="Transmission" v={(r.transmissions || []).join(", ") || "Any"} />
        <Row k="Ownership" v={r.ownership || "Any"} />
        <Row k="Year Range" v={`${r.year_min || "—"} – ${r.year_max || "—"}`} />
        <Row k="Max KM" v={(r.max_km || 0).toLocaleString()} />
      </Section>

      {/* SECTION 3 — Purchase Intent */}
      <Section title="Purchase Intent">
        <Row k="Timeline" v={r.intent || "—"} />
        <Row k="Finance Required" v={r.finance_required ? "Yes" : "No"} />
        <Row k="Exchange Available" v={r.exchange ? "Yes" : "No"} />
        <Row k="Color Preference" v={r.color || "—"} />
      </Section>

      {/* SECTION 4 — Contact Details */}
      <Section title="Contact Details">
        <Row k="Customer Name" v={r.full_name || "—"} />
        <Row k="Mobile" v={r.contact_phone || "—"} />
        <Row k="WhatsApp Preferred" v={r.whatsapp_preferred ? "Yes" : "No"} />
        <Row k="Best Contact Time" v={r.best_time || "Anytime"} />
      </Section>

      {/* Status pipeline */}
      <Section title="Pipeline">
        <div className="flex flex-wrap gap-1.5">
          {STAGES.map(s => (
            <button
              key={s.v}
              onClick={() => updateStatus(s.v)}
              data-testid={`set-status-${s.v}`}
              className={`qd-chip text-[10px] ${(r.status || "new_requirement") === s.v ? "active" : ""}`}
            >
              {s.l}
            </button>
          ))}
        </div>
      </Section>

      {/* Assignment */}
      <Section title="Assignment">
        <div className="flex flex-col gap-2">
          <select
            data-testid="req-assigned-to"
            value={r.assigned_to_user_id || ""}
            onChange={(e) => saveExecutiveById(e.target.value)}
            className="qd-input-block w-full font-mono-data text-xs"
          >
            <option value="">Unassigned</option>
            {staffUsers
              .filter(u => u.status !== "inactive" || u.id === r.assigned_to_user_id)
              .map(u => (
                <option key={u.id} value={u.id}>
                  {u.name || u.phone}{u.status === "inactive" ? " (inactive)" : ""}
                </option>
              ))}
          </select>
          <div className="flex gap-2">
            <input
              data-testid="req-executive-input"
              value={executiveInput}
              onChange={(e) => setExecutiveInput(e.target.value)}
              placeholder="Or free-text name (legacy)…"
              className="qd-input-block flex-1 text-xs"
            />
            <button data-testid="req-assign-save" onClick={saveExecutive} className="btn-secondary text-[11px] py-2 px-3">Save</button>
          </div>
        </div>
        {r.assigned_executive && (
          <div className="text-[11px] text-white/55 mt-2" data-testid="req-current-assignee">
            Currently assigned to <span className="text-white">{r.assigned_executive}</span>
          </div>
        )}
      </Section>

      {/* SECTION 5 — Internal Notes */}
      <Section title="Internal Notes">
        <div className="space-y-2 mb-3 max-h-40 overflow-y-auto">
          {(r.notes || []).slice().reverse().map((n) => (
            <div key={`${n.at}-${n.by}`} className="border-l-2 border-[#2A2A2A] pl-3 py-1">
              <div className="text-[10px] font-mono-data tracking-widest text-white/40">{n.by} · {timeAgo(n.at)} ago</div>
              <div className="text-sm text-white/85">{n.text}</div>
            </div>
          ))}
          {(!r.notes || r.notes.length === 0) && <div className="text-xs text-white/40">No notes yet.</div>}
        </div>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Internal note (visible only to admins)…"
          className="qd-input-block min-h-[60px]"
          data-testid="req-note-input"
        />
        <button onClick={addNote} className="btn-secondary text-xs py-2 px-4 mt-2" data-testid="req-add-note">Add Note</button>
      </Section>

      {/* Suggested matches (existing functionality, kept) */}
      <Section title={`Suggested Matches · ${suggestions.length}`}>
        <div className="space-y-2">
          {suggestions.slice(0, 8).map(s => (
            <div key={s.vehicle.id} className="bg-[#0F0F0F] border border-[#2A2A2A] p-3">
              <div className="flex items-center justify-between">
                <div className="font-bold text-white">{s.vehicle.make} {s.vehicle.model}</div>
                <div className="font-display text-[#FF1E1E] font-extrabold">{s.match_pct}%</div>
              </div>
              <div className="text-[11px] text-white/55 mt-1">{s.vehicle.year} · {s.vehicle.fuel_type} · {s.vehicle.transmission} · {fmtINR(s.vehicle.expected_price)}</div>
              <div className="text-[10px] font-mono-data tracking-widest text-white/45 mt-1.5 uppercase">{(s.reasons || []).join(" · ")}</div>
            </div>
          ))}
          {suggestions.length === 0 && <div className="text-xs text-white/40">No vehicles match yet.</div>}
        </div>
      </Section>

      {/* Footer: ops hotline using the canonical WA number, never the dead number */}
      <div className="pt-3 border-t border-[#1A1A1A] text-[10px] font-mono-data tracking-widest text-white/35">
        Ops hotline · <a href={waLink("Q Drives Ops escalation")} target="_blank" rel="noopener noreferrer" className="text-white/55 hover:text-[#FF1E1E]">+91 {WA_NUMBER.slice(2, 7)} {WA_NUMBER.slice(7)}</a>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <div className="qd-overline mb-2">{title}</div>
      <div className="grid grid-cols-1 gap-1.5 font-mono-data text-xs">{children}</div>
    </section>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-[#1A1A1A] py-1.5">
      <span className="text-white/40 uppercase tracking-widest text-[10px] shrink-0">{k}</span>
      <span className="text-white text-right break-words">{v}</span>
    </div>
  );
}
